// Structured data for the gp85 pages (schema.org JSON-LD), built from the
// season. worker.js puts it into the served page for crawlers that don't run
// the app; the pages put the same thing in after a client-side navigation.
// No React here: worker.js imports this file.

import { NEW_HOST, SITE_URL } from "../siteAddress.js";
import { SHOW_NAME, gp85Path, nightOf, nights } from "./season.js";

const SCHEMA = "https://schema.org";
const GREG_ID = `${SITE_URL}/#greg`;
const SHOW_ID = `${SITE_URL}${gp85Path("/")}#show`;
const SHARE_IMAGE = `${SITE_URL}/assets/share/live-booth.jpg`;

export const greg = () => ({
  "@type": "Person",
  "@id": GREG_ID,
  name: "Greg Pryor",
  alternateName: "Gregory Russell Pryor",
  jobTitle: "Former Major League Baseball player",
  url: `${SITE_URL}/`,
  image: `${SITE_URL}/assets/greg-yankees.jpg`,
  sameAs: [
    "https://www.facebook.com/GregPryor85/",
    "https://www.amazon.com/Day-Yankees-Made-Me-Shave-ebook/dp/B08FV8TLGW",
  ],
});

// One night as a broadcast event: online, free, with Greg performing. The
// fields Google's event results ask for: name, start and end, attendance
// mode, status, a virtual location, image, description, offers, organizer.
export function broadcastEvent(night, { full = true } = {}) {
  const n = typeof night === "string" ? nightOf(night) : night;
  const url = `${SITE_URL}${n.path}`;
  const ev = {
    "@type": "BroadcastEvent",
    "@id": `${url}#event`,
    name: `${SHOW_NAME}: ${n.title}`,
    description: `${n.blurb} Greg Pryor calls the original broadcast live from his living room, free, on ${NEW_HOST}.`,
    startDate: n.start,
    endDate: n.end,
    eventAttendanceMode: `${SCHEMA}/OnlineEventAttendanceMode`,
    eventStatus: `${SCHEMA}/EventScheduled`,
    isLiveBroadcast: true,
    isAccessibleForFree: true,
    url,
    image: SHARE_IMAGE,
    location: { "@type": "VirtualLocation", url: `${SITE_URL}/live` },
    performer: { "@id": GREG_ID },
    organizer: { "@id": GREG_ID },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: `${SCHEMA}/InStock`,
      url,
      validFrom: "2026-10-28T00:00:00-05:00",
    },
  };
  if (full) {
    ev.about = {
      "@type": "SportsEvent",
      name: n.tape.title,
      startDate: n.tape.date,
      location: { "@type": "Place", name: n.where },
      awayTeam: { "@type": "SportsTeam", name: n.teams.away.club },
      homeTeam: { "@type": "SportsTeam", name: n.teams.home.club },
    };
  }
  return ev;
}

export const breadcrumbs = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({
    "@type": "ListItem",
    position: i + 1,
    name,
    item: `${SITE_URL}${path}`,
  })),
});

// The home page: the site, Greg, and the whole winter as one series of nights.
export function homeJsonLd() {
  return {
    "@context": SCHEMA,
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: `Greg Pryor · ${SHOW_NAME}`,
        publisher: { "@id": GREG_ID },
      },
      greg(),
      {
        "@type": "EventSeries",
        "@id": SHOW_ID,
        name: `${SHOW_NAME}: Greg Pryor's winter broadcasts`,
        description:
          "Every Thursday night this winter, former Royals infielder Greg Pryor puts on one of his old games and calls it pitch by pitch from his living room, free, with the original TV broadcast playing in step on every screen.",
        url: `${SITE_URL}${gp85Path("/")}`,
        image: SHARE_IMAGE,
        organizer: { "@id": GREG_ID },
        performer: { "@id": GREG_ID },
        eventAttendanceMode: `${SCHEMA}/OnlineEventAttendanceMode`,
        location: { "@type": "VirtualLocation", url: `${SITE_URL}/live` },
        subEvent: nights().map((n) => broadcastEvent(n, { full: false })),
      },
    ],
  };
}

// One night's page: its event, who's on it, and where the page sits.
export function gameJsonLd(slug) {
  const n = nightOf(slug);
  if (!n) return null;
  return {
    "@context": SCHEMA,
    "@graph": [
      greg(),
      { ...broadcastEvent(n), superEvent: { "@id": SHOW_ID } },
      breadcrumbs([
        ["Greg Pryor", gp85Path("/")],
        ["This winter", `${gp85Path("/")}#winter`],
        [n.title, n.path],
      ]),
    ],
  };
}

export function albumJsonLd() {
  return {
    "@context": SCHEMA,
    "@graph": [
      greg(),
      {
        "@type": "ImageGallery",
        "@id": `${SITE_URL}${gp85Path("/album")}#gallery`,
        name: "Greg Pryor's album",
        description:
          "Thirty-one photos and a film from Greg Pryor's life in baseball: Marietta, the minors, Comiskey Park, Kansas City and the 1985 World Series.",
        url: `${SITE_URL}${gp85Path("/album")}`,
        about: { "@id": GREG_ID },
      },
      breadcrumbs([
        ["Greg Pryor", gp85Path("/")],
        ["The album", gp85Path("/album")],
      ]),
    ],
  };
}
